import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Select,
  NumberInput,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle } from "@tabler/icons-react";

const countDescendants = entity => {
  const children = Array.isArray(entity?.operations) ? entity.operations : [];
  return children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
};

const EditOperatingLevelEntityModal = ({
  opened,
  onClose,
  onSave,
  onDeactivate,
  entity,
  currentParentId = null,
  entityList = [],
  operationTypes = [],
}) => {
  const currentName = entity?.name || "";
  const currentTypeId = entity?.operation_type?.id ?? null;
  const isTopType = entity?.operation_type?.top_operation_type === true;
  const showTypeSelect = !!entity && !isTopType;

  const [name, setName] = useState(currentName);
  const [parentId, setParentId] = useState(
    currentParentId ? String(currentParentId) : null
  );
  const [typeId, setTypeId] = useState(
    currentTypeId ? String(currentTypeId) : null
  );
  const [subdomain, setSubdomain] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [authKey, setAuthKey] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [authMethod, setAuthMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (opened) {
      const connection = entity?.fieldroutes_connection;
      setName(currentName);
      setParentId(currentParentId ? String(currentParentId) : null);
      setTypeId(currentTypeId ? String(currentTypeId) : null);
      setSubdomain(connection?.subdomain || "");
      setOfficeId(connection?.fr_office_id ?? "");
      setAuthKey(connection?.auth_key || "");
      setAuthToken(connection?.auth_token || "");
      setAuthMethod(connection?.auth_method || null);
      setError("");
      setConfirmingDeactivate(false);
    }
  }, [opened, entity, currentName, currentParentId, currentTypeId]);

  const parentOptions = useMemo(() => {
    const typeOrder = new Map();
    operationTypes.forEach((t, idx) => {
      typeOrder.set(String(t.id), idx);
      if (t.name != null) typeOrder.set(t.name, idx);
    });
    const currentOrder =
      typeOrder.get(String(currentTypeId)) ??
      typeOrder.get(entity?.operation_type?.name);

    return entityList
      .filter(e => e.id !== entity?.id)
      .filter(e => {
        if (currentOrder == null) return true;
        const order =
          typeOrder.get(String(e.operation_type_id)) ??
          typeOrder.get(e.operation_type_name);
        // Only allow parents whose type is higher (earlier in the list).
        return order != null && order < currentOrder;
      })
      .map(e => ({
        value: String(e.id),
        label: `${e.operation_type_name}: ${e.name}`,
      }));
  }, [entityList, entity, operationTypes, currentTypeId]);

  const showParentSelect = parentOptions.length > 0 && !isTopType;
  const descendantCount = useMemo(() => countDescendants(entity), [entity]);
  const hasDescendants = descendantCount > 0;
  const isTopLevel = currentParentId == null;

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (showParentSelect && !parentId) {
      setError("Parent operation is required");
      return;
    }
    if (showTypeSelect && !typeId) {
      setError("Operation type is required");
      return;
    }
    if (isSelectedBaseType && authMethod === "api") {
      if (!subdomain.trim()) {
        setError("Subdomain is required for API auth");
        return;
      }
      if (officeId === null || officeId === "") {
        setError("Office ID is required for API auth");
        return;
      }
      if (!authKey.trim()) {
        setError("Auth key is required for API auth");
        return;
      }
      if (!authToken.trim()) {
        setError("Auth token is required for API auth");
        return;
      }
    }
    setLoading(true);
    setError("");
    try {
      await onSave({
        name: name.trim(),
        operationParentId: parentId ? Number(parentId) : null,
        operationTypeId: showTypeSelect && typeId ? Number(typeId) : null,
        ...(isSelectedBaseType && {
          subdomain: subdomain.trim() || null,
          fr_office_id: officeId ? Number(officeId) : null,
          auth_key: authKey.trim() || null,
          auth_token: authToken.trim() || null,
          auth_method: authMethod,
        }),
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    setDeactivating(true);
    setError("");
    try {
      await onDeactivate();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to deactivate");
      setConfirmingDeactivate(false);
    } finally {
      setDeactivating(false);
    }
  };

  const handleClose = () => {
    setError("");
    setConfirmingDeactivate(false);
    onClose();
  };

  const selectedType = operationTypes.find(t => String(t.id) === typeId);
  const isSelectedBaseType = selectedType?.base_operation_type === true;

  const connection = entity?.fieldroutes_connection;
  const nameUnchanged = name.trim() === currentName.trim();
  const parentUnchanged =
    (parentId ? Number(parentId) : null) === (currentParentId ?? null);
  const typeUnchanged =
    (typeId ? Number(typeId) : null) === (currentTypeId ?? null);
  const connectionUnchanged =
    (subdomain.trim() || "") === (connection?.subdomain || "") &&
    (officeId ?? "") === (connection?.fr_office_id ?? "") &&
    (authKey.trim() || "") === (connection?.auth_key || "") &&
    (authToken.trim() || "") === (connection?.auth_token || "") &&
    (authMethod || null) === (connection?.auth_method || null);
  const isUnchanged =
    nameUnchanged &&
    parentUnchanged &&
    typeUnchanged &&
    (!isSelectedBaseType || connectionUnchanged);
  const busy = loading || deactivating;

  const showCombineWarning =
    showTypeSelect && isSelectedBaseType && !typeUnchanged && hasDescendants;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Operation"
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {confirmingDeactivate ? (
          <>
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="red"
              variant="light"
              title="Deactivate this operation?"
            >
              <Text size="sm">
                Are you sure you want to deactivate{" "}
                <strong>{currentName}</strong>?
              </Text>
              {hasDescendants && (
                <Text size="sm" mt="xs" fw={600}>
                  Warning: this will also deactivate the {descendantCount}{" "}
                  operation{descendantCount === 1 ? "" : "s"} underneath it.
                </Text>
              )}
            </Alert>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setConfirmingDeactivate(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleConfirmDeactivate}
                loading={deactivating}
              >
                Deactivate
              </Button>
            </Group>
          </>
        ) : (
          <>
            <TextInput
              label="Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            {showParentSelect && (
              <Select
                label="Parent Operation"
                placeholder="Select parent operation"
                data={parentOptions}
                value={parentId}
                onChange={setParentId}
                searchable
                required
              />
            )}
            {showTypeSelect && (
              <Select
                label="Operation Type"
                placeholder="Select operation type"
                data={operationTypes.map(t => ({
                  value: String(t.id),
                  label: t.name,
                }))}
                value={typeId}
                onChange={setTypeId}
                required
              />
            )}
            {isSelectedBaseType && (
              <>
                <TextInput
                  label={`Subdomain${authMethod === "api" ? " *" : ""}`}
                  placeholder="Enter subdomain"
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value)}
                />

                <NumberInput
                  label={`Office ID${authMethod === "api" ? " *" : ""}`}
                  placeholder="Enter office ID"
                  value={officeId}
                  onChange={setOfficeId}
                />

                <TextInput
                  label={`Auth Key${authMethod === "api" ? " *" : ""}`}
                  placeholder="Enter auth key"
                  value={authKey}
                  onChange={e => setAuthKey(e.target.value)}
                />

                <TextInput
                  label={`Auth Token${authMethod === "api" ? " *" : ""}`}
                  placeholder="Enter auth token"
                  value={authToken}
                  onChange={e => setAuthToken(e.target.value)}
                />

                <Select
                  label="Auth Method"
                  placeholder="Select auth method"
                  data={[
                    { value: "api", label: "API" },
                    { value: "csv", label: "CSV" },
                  ]}
                  value={authMethod}
                  onChange={setAuthMethod}
                  clearable
                />
              </>
            )}
            {showCombineWarning && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="orange"
                variant="light"
              >
                This will combine all sub groups into one {selectedType.name}.
                This cannot be undone.
              </Alert>
            )}
            <Group justify="space-between" gap="sm">
              {onDeactivate && !isTopLevel ? (
                <Button
                  color="red"
                  variant="outline"
                  onClick={() => setConfirmingDeactivate(true)}
                  disabled={busy}
                >
                  Deactivate
                </Button>
              ) : (
                <span />
              )}
              <Group gap="sm">
                <Button variant="outline" onClick={handleClose} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  loading={loading}
                  disabled={!name.trim() || isUnchanged || busy}
                >
                  Save
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default EditOperatingLevelEntityModal;
